import { useState } from "react";
import {
  PanelShell, Badge, Button, TextField, PillToggleGroup, ApiResourceView,
  DJSEMApiClient, useApiResource,
  confidenceTier, confidenceTone, recommendationLabel, evidenceSummaryLine, byStrongestFirst,
  type DJSEMSetLearningScoreDTO,
} from "../index";

/**
 * Audience Feedback Intelligence / Set Learning Engine module (prompt 150). Surfaces platform
 * import status, scored tracks/phrases/transitions with evidence-framed summaries (never "AI
 * knows" — see `feedbackIntelligenceModel.ts`), and live QR feedback session management. Raw
 * metrics/recommendations always show their evidence count + confidence tier alongside them,
 * per the spec's "raw metrics and AI summaries are visually separate" + "no overclaiming" rules.
 *
 * @param client - The API client used to fetch feedback data.
 * @returns The Platform status, Set Learning scores, and Live feedback panels.
 */
export function FeedbackIntelligenceModule({ client }: { client: DJSEMApiClient }) {
  return (
    <>
      <PlatformStatusPanel client={client} />
      <SetLearningScoresPanel client={client} />
      <LiveFeedbackPanel client={client} />
    </>
  );
}

function PlatformStatusPanel({ client }: { client: DJSEMApiClient }) {
  const res = useApiResource(() => client.feedbackPlatformStatus(), []);
  return (
    <PanelShell title="Platform import status">
      <ApiResourceView
        resource={res}
        empty={{ title: "No platforms yet", detail: "Import SoundCloud timed comments or a YouTube analytics CSV to get started." }}
        render={(data) => (
          <>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
              {data.platforms.map((p) => (
                <div key={p.platform} style={{ display: "flex", flexDirection: "column", gap: 2, padding: "8px 12px", border: "1px solid #333", borderRadius: 6, minWidth: 180 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                    <strong style={{ fontSize: 12, textTransform: "capitalize" }}>{p.platform}</strong>
                    <Badge tone={p.status === "connected" ? "ok" : "neutral"}>{p.status === "connected" ? "connected" : "manual import"}</Badge>
                  </div>
                  {p.note ? <span style={{ fontSize: 10, opacity: 0.65 }}>{p.note}</span> : null}
                </div>
              ))}
            </div>
            <div style={{ fontSize: 11, opacity: 0.7, marginBottom: 6 }}>Recent import jobs</div>
            {data.recentImportJobs.length === 0 ? <div style={{ fontSize: 11, opacity: 0.5 }}>No imports yet.</div> : null}
            {data.recentImportJobs.map((j) => (
              <div key={j.id} style={{ display: "flex", justifyContent: "space-between", fontSize: 11, padding: "3px 0" }}>
                <span>{j.platform} · {j.source} · {j.itemsImported} imported</span>
                <Badge tone={j.status === "succeeded" ? "ok" : j.status === "failed" ? "warn" : "neutral"}>{j.status}</Badge>
              </div>
            ))}
          </>
        )}
      />
    </PanelShell>
  );
}

const TARGET_TYPES = ["track", "phrase", "transition", "set_phase", "visual_moment"] as const;

function SetLearningScoresPanel({ client }: { client: DJSEMApiClient }) {
  const [targetType, setTargetType] = useState<(typeof TARGET_TYPES)[number]>("track");
  const res = useApiResource(() => client.listFeedbackScores(targetType), [targetType], (d) => (d.items?.length ?? 0) === 0);
  return (
    <PanelShell title="Set Learning — scores &amp; recommendations" style={{ marginTop: 12 }}>
      <div style={{ marginBottom: 10 }}>
        <PillToggleGroup options={TARGET_TYPES} value={targetType} onChange={setTargetType} labelFor={(t) => t.replace(/_/g, " ")} />
      </div>
      <ApiResourceView
        resource={res}
        empty={{ title: "No scores yet", detail: "Run scoring after importing SoundCloud/YouTube feedback or collecting live feedback for this target type." }}
        render={(data) => (
          <>
            {[...data.items].sort(byStrongestFirst).map((s) => <ScoreRow key={s.id} score={s} client={client} onDecided={res.refetch} />)}
          </>
        )}
      />
    </PanelShell>
  );
}

function ScoreRow({ score, client, onDecided }: { score: DJSEMSetLearningScoreDTO; client: DJSEMApiClient; onDecided: () => void }) {
  const [busy, setBusy] = useState(false);
  const decide = async (decision: "accepted" | "rejected") => {
    setBusy(true);
    await client.decideFeedbackRecommendation(score.id, decision);
    setBusy(false);
    onDecided();
  };
  return (
    <div style={{ borderTop: "1px solid #2a2a2a", padding: "10px 0", display: "flex", flexDirection: "column", gap: 4 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontSize: 12, fontWeight: 600 }}>{score.targetId}</span>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <Badge tone={confidenceTone(score.confidence)}>confidence: {confidenceTier(score.confidence)}</Badge>
          <Badge tone="neutral">{recommendationLabel(score.recommendation)}</Badge>
        </div>
      </div>
      <div style={{ display: "flex", gap: 10, fontSize: 10, opacity: 0.7 }}>
        {score.soundCloudScore !== null ? <span>SoundCloud {score.soundCloudScore.toFixed(2)}</span> : null}
        {score.youtubeScore !== null ? <span>YouTube {score.youtubeScore.toFixed(2)}</span> : null}
        {score.liveFeedbackScore !== null ? <span>Live {score.liveFeedbackScore.toFixed(2)}</span> : null}
        <span>Combined {score.combinedScore.toFixed(2)}</span>
      </div>
      <div style={{ fontSize: 11, opacity: 0.8 }}>{evidenceSummaryLine(score.evidenceCount, score.confidence)}</div>
      {score.reasons.length > 0 ? <div style={{ fontSize: 10, opacity: 0.6 }}>{score.reasons.join(" · ")}</div> : null}
      {score.recommendation !== "needs_more_data" ? (
        <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
          <Button onClick={() => decide("accepted")} disabled={busy}>Accept (draft only)</Button>
          <Button onClick={() => decide("rejected")} disabled={busy}>Reject</Button>
        </div>
      ) : null}
    </div>
  );
}

function LiveFeedbackPanel({ client }: { client: DJSEMApiClient }) {
  const res = useApiResource(() => client.listLiveFeedbackSessions(), []);
  const [sessionRef, setSessionRef] = useState("");
  const [creating, setCreating] = useState(false);

  const create = async () => {
    if (!sessionRef.trim()) return;
    setCreating(true);
    await client.createLiveFeedbackSession(sessionRef.trim());
    setSessionRef("");
    setCreating(false);
    res.refetch();
  };

  return (
    <PanelShell title="Live feedback links" style={{ marginTop: 12 }}>
      <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
        <TextField value={sessionRef} onChange={setSessionRef} placeholder="Live session id / set name" style={{ flex: 1 }} />
        <Button onClick={create} disabled={creating || !sessionRef.trim()} variant="primary">Create QR link</Button>
      </div>
      <div style={{ fontSize: 10, opacity: 0.6, marginBottom: 8 }}>
        Audience feedback is anonymous — no account or personal data is required to submit.
      </div>
      <ApiResourceView
        resource={res}
        empty={{ title: "No live feedback links yet", detail: "Create one before your next set to collect anonymous audience/promoter feedback." }}
        render={(data) => (
          <>
            {data.items.map((s) => (
              <div key={s.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderTop: "1px solid #2a2a2a" }}>
                <span style={{ fontSize: 12 }}>{s.session_ref}</span>
                <code style={{ fontSize: 10, opacity: 0.7 }}>/feedback/{s.feedback_token}</code>
              </div>
            ))}
          </>
        )}
      />
    </PanelShell>
  );
}
